import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"

// Model Objects

export class StockLocation extends ModelObjectWithDirtyCheck<StockLocation> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    /**
    * Address 1
    * Not Nullable
    * Max Length : 1000
    */
    public addressLine1: string = "";
    /**
    * Address 2
    * Not Nullable
    * Max Length : 1000
    */
    public addressLine2: string = "";
    /**
    * Address 3
    * Not Nullable
    * Max Length : 1000
    */
    public addressLine3: string = "";
    /**
    * Address 4
    * Not Nullable
    * Max Length : 1000
    */
    public addressLine4: string = "";
    /**
    * Address 5
    * Not Nullable
    * Max Length : 1000
    */
    public addressLine5: string = "";
    /**
    * Address 6
    * Not Nullable
    * Max Length : 1000
    */
    public addressLine6: string = "";
    /**
    * Bonded
    */
    public isBonded: boolean = false;
    /**
    * Contact
    * Not Nullable
    * Max Length : 1000
    */
    public contactName: string = "";
    /**
    * Default Location
    */
    public isDefault: boolean = false;
    /**
    * Name
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public name: string = "";
    /**
    * Email
    * Not Nullable
    * Max Length : 1000
    */
    public emailAddress: string = "";
    /**
    * Postcode
    * Not Nullable
    * Max Length : 1000
    */
    public postcode: string = "";
    /**
    * Telephone
    * Not Nullable
    * Max Length : 1000
    */
    public phoneNumber: string = "";
    public isHidden: boolean = false;
    /**
    * Tax code for this warehouse, used for duty schemes where duty reporting is required to be broken down by warehouse.
    * Not Nullable
    * Max Length : 1000
    */
    public warehouseTaxCode: string = "";
    /**
    * The distribution centre which distributes stock from this location.
    */
    public siteId: Guid = Guid.createEmpty();
    /**
    * Whether the stock location is an external stock location.
    */
    public isExternal: boolean = false;
    /**
    * Default Location for production batches.
    */
    public isDefaultForProduction: boolean = false;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.addressLine1 = jsonSerializedObject.addressLine1;
          this.addressLine2 = jsonSerializedObject.addressLine2;
          this.addressLine3 = jsonSerializedObject.addressLine3;
          this.addressLine4 = jsonSerializedObject.addressLine4;
          this.addressLine5 = jsonSerializedObject.addressLine5;
          this.addressLine6 = jsonSerializedObject.addressLine6;
          this.isBonded = jsonSerializedObject.isBonded;
          this.contactName = jsonSerializedObject.contactName;
          this.isDefault = jsonSerializedObject.isDefault;
          this.name = jsonSerializedObject.name;
          this.emailAddress = jsonSerializedObject.emailAddress;
          this.postcode = jsonSerializedObject.postcode;
          this.phoneNumber = jsonSerializedObject.phoneNumber;
          this.isHidden = jsonSerializedObject.isHidden;
          this.warehouseTaxCode = jsonSerializedObject.warehouseTaxCode;
          this.siteId = Guid.parseOrEmpty(jsonSerializedObject.siteId);
          this.isExternal = jsonSerializedObject.isExternal;
          this.isDefaultForProduction = jsonSerializedObject.isDefaultForProduction;
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.addressLine1 = this.addressLine1;
        toRet.addressLine2 = this.addressLine2;
        toRet.addressLine3 = this.addressLine3;
        toRet.addressLine4 = this.addressLine4;
        toRet.addressLine5 = this.addressLine5;
        toRet.addressLine6 = this.addressLine6;
        toRet.isBonded = this.isBonded;
        toRet.contactName = this.contactName;
        toRet.isDefault = this.isDefault;
        toRet.name = this.name;
        toRet.emailAddress = this.emailAddress;
        toRet.postcode = this.postcode;
        toRet.phoneNumber = this.phoneNumber;
        toRet.isHidden = this.isHidden;
        toRet.warehouseTaxCode = this.warehouseTaxCode;
        toRet.siteId = this.siteId.toString();
        toRet.isExternal = this.isExternal;
        toRet.isDefaultForProduction = this.isDefaultForProduction;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "StockLocation.Id cannot be empty"},
            {field: "addressLine1", rule: (v: any) => v == null || v.length <= 1000 || "AddressLine1 has too many characters (1000)"},
            {field: "addressLine2", rule: (v: any) => v == null || v.length <= 1000 || "AddressLine2 has too many characters (1000)"},
            {field: "addressLine3", rule: (v: any) => v == null || v.length <= 1000 || "AddressLine3 has too many characters (1000)"},
            {field: "addressLine4", rule: (v: any) => v == null || v.length <= 1000 || "AddressLine4 has too many characters (1000)"},
            {field: "addressLine5", rule: (v: any) => v == null || v.length <= 1000 || "AddressLine5 has too many characters (1000)"},
            {field: "addressLine6", rule: (v: any) => v == null || v.length <= 1000 || "AddressLine6 has too many characters (1000)"},
            {field: "contactName", rule: (v: any) => v == null || v.length <= 1000 || "ContactName has too many characters (1000)"},
            {field: "name", rule: (v: any) => v == null || v.length !== 0 || "Name cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length <= 1000 || "Name has too many characters (1000)"},
            {field: "emailAddress", rule: (v: any) => v == null || v.length <= 1000 || "EmailAddress has too many characters (1000)"},
            {field: "postcode", rule: (v: any) => v == null || v.length <= 1000 || "Postcode has too many characters (1000)"},
            {field: "phoneNumber", rule: (v: any) => v == null || v.length <= 1000 || "PhoneNumber has too many characters (1000)"},
            {field: "warehouseTaxCode", rule: (v: any) => v == null || v.length <= 1000 || "WarehouseTaxCode has too many characters (1000)"},
            {field: "siteId", rule: (v: any) => !v.isEmpty() || "StockLocation.SiteId cannot be empty"},
    ];
}


export class StockLocationService extends ServiceBase {

  public async getAllStockLocations(tenantId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(StockLocation)[]> {
    const req = {
    tenantId:tenantId.toString(),
    };
    const extractor = (response:any) => response.stockLocations.map((v: any) => new StockLocation(v));;
    return this.makeJsonRequest("stockLocation/v1/getAllStockLocations", req, false, extractor, opts);
  };

  public async getStockLocation(stockLocationId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<StockLocation> {
    const req = {
    tenantId:api.tenantId.toString(),
    stockLocationId:stockLocationId.toString(),
    };
    const extractor = (response:any) => new StockLocation(response.stockLocation);;
    return this.makeJsonRequest("stockLocation/v1/getStockLocation", req, false, extractor, opts);
  };

  public async getStockLocations(stockLocationIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(StockLocation)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    stockLocationIds:stockLocationIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.stockLocations.map((v: any) => new StockLocation(v));;
    return this.makeJsonRequest("stockLocation/v1/getStockLocations", req, false, extractor, opts);
  };

  public async saveStockLocations(stockLocations: readonly StockLocation[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    stockLocations:stockLocations.map(v => v.toJsonSerializedObject()),
    };
    const extractor = null;
    return this.makeJsonRequest("stockLocation/v1/saveStockLocations", req, true, extractor, opts);
  };

  public async setStockLocationIsHidden(stockLocationId: Guid, hide: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    stockLocationId:stockLocationId.toString(),
    hide:hide,
    };
    const extractor = null;
    return this.makeJsonRequest("stockLocation/v1/setStockLocationIsHidden", req, true, extractor, opts);
  };
}

export const stockLocationService = new StockLocationService();